'use client';

import { Sparkles, FileText, Target, Zap, ClipboardList, LayoutTemplate, Clock } from 'lucide-react';
import { ScrollReveal } from '@/components/ScrollReveal';

const steps = [
  {
    num: '1',
    title: 'Paste the Job Description',
    desc: 'Copy any job posting and paste it in. Our AI instantly identifies the key requirements, skills, and keywords the employer is looking for.',
  },
  {
    num: '2',
    title: 'AI Tailors Your Resume',
    desc: 'Using your uploaded career history, the AI writes a resume that highlights your most relevant experience and matches the exact language of the job posting.',
  },
  {
    num: '3',
    title: 'Download & Apply',
    desc: 'Review your tailored resume, make any final tweaks, and download a polished PDF ready to submit. The whole process takes under two minutes.',
  },
];

const features = [
  {
    icon: Target,
    title: 'Tailored Keywords',
    desc: 'Every resume is built around the specific keywords and phrases from the job description so you match what ATS systems scan for.',
  },
  {
    icon: FileText,
    title: 'ATS-Optimized Formatting',
    desc: 'Clean, parseable layouts that pass through applicant tracking systems without losing content or breaking formatting.',
  },
  {
    icon: LayoutTemplate,
    title: 'Multiple Templates',
    desc: 'Choose from professional templates designed for different industries — tech, finance, healthcare, creative, and more.',
  },
  {
    icon: Sparkles,
    title: 'Powered by Your History',
    desc: 'Unlike generic builders, we use your actual career documents and experience to write authentic, specific bullet points.',
  },
  {
    icon: Zap,
    title: 'One-Click Generation',
    desc: 'No endless form-filling. Upload your documents once, then generate a new tailored resume for every application with a single click.',
  },
  {
    icon: ClipboardList,
    title: 'Cover Letter Included',
    desc: 'Get a matching cover letter alongside your resume — both tailored to the same job description for a cohesive application.',
  },
];

const comparisons = [
  { aspect: 'Time per resume', manual: '45–90 minutes', ai: 'Under 2 minutes' },
  { aspect: 'Keyword matching', manual: 'Manual guesswork', ai: 'Automatic from job description' },
  { aspect: 'ATS optimization', manual: 'Hope for the best', ai: 'Built-in scoring & formatting' },
  { aspect: 'Tailored per job', manual: 'Copy-paste & tweak', ai: 'Fully rewritten each time' },
  { aspect: 'Consistency', manual: 'Varies by effort', ai: 'Professional every time' },
  { aspect: 'Cover letter', manual: 'Separate process', ai: 'Generated together' },
];

const faqs = [
  {
    q: 'Is the AI resume builder really free?',
    a: 'Yes. The Free plan includes 3 tailored resumes per month with basic ATS match scoring. No credit card required to sign up.',
  },
  {
    q: 'How does the AI tailor my resume to each job?',
    a: 'You upload your career documents (old resumes, LinkedIn export, performance reviews, etc.) once. When you paste a job description, the AI analyzes the requirements and rewrites your resume to emphasize matching skills, experience, and keywords from your actual history.',
  },
  {
    q: 'Will my resume pass ATS systems?',
    a: 'Yes. Every resume is generated with ATS-friendly formatting — clean section headers, standard fonts, and parseable layouts. The Pro plan also includes an ATS match score so you can see exactly how well you match before applying.',
  },
  {
    q: 'Can I edit the resume after generation?',
    a: 'Absolutely. You can edit any section directly, chat with the AI to request specific changes, or use the auto-improve feature to fix keyword gaps and strengthen weak bullet points.',
  },
  {
    q: 'What file formats can I download?',
    a: 'Resumes are available as PDF downloads. The formatting is optimized for both human readers and ATS parsing.',
  },
];

export default function AIResumeBuilderContent() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-5xl mx-auto">
        {/* Hero */}
        <ScrollReveal>
          <section className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Resume Builder
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-6">
              Build a Tailored Resume<br className="hidden md:block" /> in Under 2 Minutes
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Paste any job description and let our AI create an ATS-optimized resume
              from your real career history. No templates to fill out. No guesswork.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.superpowerresume.com"
                className="inline-block bg-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg hover:bg-blue-700 hover:scale-105 hover:shadow-xl transition-all duration-200"
              >
                Try Free — No Credit Card
              </a>
            </div>
          </section>
        </ScrollReveal>

        {/* How It Works */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step) => (
                <div key={step.num} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.num}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {/* Key Features */}
        <section className="mb-20">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
            Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              AI Resume Builder vs. Manual Writing
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              See why thousands of job seekers are switching to AI-powered resume building.
            </p>
            <div className="rounded-2xl shadow-lg bg-white border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left font-bold text-gray-800" />
                      <th className="px-6 py-4 text-center font-bold text-gray-500 w-48">
                        <div className="flex items-center justify-center gap-2">
                          <Clock className="w-4 h-4" /> Manual
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center font-bold text-blue-700 w-48">
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles className="w-4 h-4" /> Superpower Resume
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map(({ aspect, manual, ai }, idx) => (
                      <tr
                        key={aspect}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                      >
                        <td className="px-6 py-4 font-medium text-gray-900">{aspect}</td>
                        <td className="px-6 py-4 text-center text-gray-500">{manual}</td>
                        <td className="px-6 py-4 text-center text-blue-700 font-medium">{ai}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* FAQ */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map(({ q, a }) => (
                <details
                  key={q}
                  className="group bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  <summary className="cursor-pointer px-6 py-5 text-lg font-semibold text-gray-900 flex items-center justify-between list-none">
                    {q}
                    <span className="ml-4 text-gray-400 group-open:rotate-45 transition-transform duration-200 text-2xl leading-none">
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed">{a}</div>
                </details>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {/* Final CTA */}
        <ScrollReveal>
          <section className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center">
              <h2 className="text-3xl font-extrabold mb-4 text-center text-white tracking-tight">
                Ready to build your next resume?
              </h2>
              <p className="text-lg text-blue-100 mb-8 text-center">
                Join thousands of job seekers using AI to land more interviews. Free to start — no credit card required.
              </p>
              <a
                href="https://app.superpowerresume.com"
                className="inline-block bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow text-lg hover:bg-blue-50 hover:scale-105 hover:shadow-xl transition-all duration-200"
              >
                Get Started Free
              </a>
            </div>
          </section>
        </ScrollReveal>
      </main>
    </div>
  );
}
